import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMapGL from 'react-map-gl';
import { I18n } from 'react-redux-i18n';
import { Link } from 'react-router-dom';
import reduce from 'lodash/reduce';

import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Select from './components/Select';
import Header from 'components/Header';
import SynthesisDistributionContainer from './container';

import { useUserMe } from 'apiTyped/hooks/users.query';
import {
  getMarketsForRegion,
  useDistributionSynthesisFilters,
  useDistributionSynthesisQuery
} from 'apiTyped/hooks/distribution.query';
import toOptionsList from 'utils/valuesToOptions';

const isMobile = window.innerWidth <= 845;

const DistributionLeftContent = ({ data }) => {
  const { restaurants, caves, websites } = data;

  const renderIndicator = (label, value) => (
    <div className="synthesisLeftIndicator">
      <p className="synthesisIndicatorLabel">{label}</p>
      <p className="synthesisLeftIndicatorValue">{value}</p>
    </div>
  );

  return (
    <div className="synthesisLeftContent">
      <div className="synthesisContentTitle">
        <h2>{I18n.t('synthesisDistribution.leftIndicators.title')}</h2>
        <div className="synthesisContentLine" />
      </div>
      <div className="indicateurWrapper">
        {restaurants
          ? renderIndicator(
              I18n.t('synthesisDistribution.leftIndicators.restaurant'),
              restaurants.total
            )
          : null}
        {caves
          ? renderIndicator(
              I18n.t('synthesisDistribution.leftIndicators.cave'),
              caves.total
            )
          : null}
        {websites
          ? renderIndicator(
              I18n.t('synthesisDistribution.leftIndicators.web'),
              websites.total
            )
          : null}
      </div>
    </div>
  );
};

const DistributionRightContent = ({ data }) => {
  const renderSynthesisRightIndicator = (label, data) => {
    if (!data) return null;

    return (
      <div className="synthesisRightIndicator">
        <div>
          <p className="synthesisIndicatorLabel">{label}</p>
          <div className="synthesisRightIndicatorValue">
            <p>
              {I18n.t('synthesisDistribution.rightIndicators.dn')} = {data?.dn}
            </p>
            <p>
              {I18n.t('synthesisDistribution.rightIndicators.tp')} = {data?.tp}
            </p>
          </div>
        </div>
        <div>
          <p className="synthesisIndicatorLabel">
            {I18n.t('synthesisDistribution.rightIndicators.rank')}
          </p>
          <p className="synthesisRightIndicatorRank">
            {data.rank}/{data.maxRank}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="synthesisRightContent">
      <div className="synthesisContentTitle">
        <h2>{I18n.t('synthesisDistribution.rightIndicators.title')}</h2>
        <div className="synthesisContentLine" />
      </div>
      <div className="synthesisContentBody">
        <div>
          {renderSynthesisRightIndicator(
            I18n.t('synthesisDistribution.leftIndicators.restaurant'),
            data.restaurants
          )}
          {renderSynthesisRightIndicator(
            I18n.t('synthesisDistribution.leftIndicators.cave'),
            data.caves
          )}
          {renderSynthesisRightIndicator(
            I18n.t('synthesisDistribution.leftIndicators.web'),
            data.websites
          )}
        </div>
      </div>
    </div>
  );
};

const SynthesisDistribution = () => {
  const { user } = useUserMe();
  const [state, setState] = useState({
    zone: 'world',
    market: 'all',
    entity: null
  });

  const viewport = useMemo(
    () => ({
      width: '100%',
      height: isMobile ? '15rem' : '100%',
      latitude: 46.227638,
      longitude: 2.213749,
      zoom: 3
    }),
    []
  );

  const { isLoadingFilters, filters } = useDistributionSynthesisFilters();

  const { isLoadingData, data } = useDistributionSynthesisQuery(
    {
      wine: !user.isBranded ? state.entity : null,
      brand: user.isBranded ? state.entity : null,
      market: state.market !== 'all' ? state.market : null,
      region: state.zone !== 'world' ? state.zone : null
    },
    state.entity !== null
  );

  const entities = useMemo(() => {
    return user.isBranded ? filters.clientBrands : filters.clientWines;
  }, [filters, user.isBranded]);

  useEffect(() => {
    if (entities.length > 0) {
      setState((prevState) => ({ ...prevState, entity: entities[0] }));
    }
  }, [entities]);

  const setZone = useCallback((zone) => {
    setState((prevState) => ({ ...prevState, zone }));
  }, []);

  const setMarket = useCallback((market) => {
    setState((prevState) => ({ ...prevState, market: market.value }));
  }, []);

  const setEntity = useCallback((entity) => {
    setState((prevState) => ({ ...prevState, entity: entity.value }));
  }, []);

  const defaultRegionOption = { value: 'world', label: I18n.t('world') };
  const zoneOptions = [defaultRegionOption, ...toOptionsList(filters.zones)];
  const marketsOptionsByZone = reduce(
    filters.zones,
    (acc, key) => {
      acc[key] = toOptionsList(filters.marketsByZone[key], 'name');
      return acc;
    },
    {}
  );

  const marketsOptions = getMarketsForRegion(state.zone, marketsOptionsByZone);
  const entitiesOptions = toOptionsList(entities);

  const selectedZone = zoneOptions?.find(
    (option) => option.value === state.zone
  );
  const selectedMarket = marketsOptions?.find(
    (option) => option.value === state.market
  );
  const selectedEntity = entitiesOptions?.find(
    (option) => option.value === state.entity
  );
  const marketsOptionsWithDefault = [...marketsOptions]?.filter(
    (item) => item.label !== 'placeholderAll'
  );

  return (
    <SynthesisDistributionContainer>
      <ReactMapGL
        {...viewport}
        style={{ position: 'fixed' }}
        mapStyle="mapbox://styles/waves59/cjwiz4hy6003w1cnx8q647swr/draft"
        mapboxAccessToken="pk.eyJ1Ijoid2F2ZXM1OSIsImEiOiJjanNnMGQ4ZG4wMW90M3lrZ2g5emRsdHo1In0.zMQjOGNuUxxsdlEUydihgg"
      />
      <div className="synthesisContainer">
        <Header
          title={I18n.t('synthesisDistribution.title')}
          subTitle={I18n.t('synthesisDistribution.subTitle')}
        >
          <div className="filtersContainer">
            <Select
              label={I18n.t('synthesisDistribution.filters.zone')}
              value={selectedZone}
              options={zoneOptions}
              onChange={(zone) => setZone(zone.value)}
              isClearable={false}
              style={{ width: '200px' }}
            />
            <Select
              label={I18n.t('synthesisDistribution.filters.market')}
              value={
                selectedMarket
                  ? {
                      ...selectedMarket,
                      label: selectedMarket.label
                    }
                  : null
              }
              options={marketsOptionsWithDefault.sort((a, b) =>
                a && b ? String(a.label).localeCompare(String(b.label)) : 0
              )}
              onChange={setMarket}
              isClearable={false}
              style={{ width: '200px' }}
            />
            <Select
              label={
                user.isBranded
                  ? I18n.t('synthesisDistribution.filters.brand')
                  : I18n.t('synthesisDistribution.filters.wine')
              }
              value={selectedEntity}
              options={entitiesOptions}
              onChange={setEntity}
              isClearable={false}
              style={{ width: '200px' }}
            />
          </div>
        </Header>
        <div className="synthesisContent">
          <DistributionLeftContent data={data} />
          <DistributionRightContent data={data} />
          <div className="linkButtonContainer">
            <Link to={`/distribution/synthesis/detail`} className="linkButton">
              <Button
                label={I18n.t(
                  'synthesisDistribution.rightIndicators.buttonToDetail'
                )}
                textTransform="uppercase"
                color="white"
                textColor="black"
                fontSize="14px"
                border="0.8px black solid"
                fontFamily="Oswald"
              />
            </Link>
          </div>
        </div>
      </div>
      <Spinner loading={isLoadingFilters || isLoadingData} />
    </SynthesisDistributionContainer>
  );
};

export default SynthesisDistribution;
